import axios from "axios";

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/agent`;

export const agentAPI = {
  getRecommendations: (collectionName) => {
    return axios.get(`${BASE_URL}/recommendations/${collectionName}`);
  },

  ask: (collectionName, question, currentPage) => {
    return axios.post(`${BASE_URL}/ask`, { collectionName, question, currentPage });
  },

  // Heatmap + user-journey -> nudge strategy spec for a draft nudge
  nudgeAdvisor: (collectionName) => {
    return axios.post(`${BASE_URL}/nudge-advisor`, { collectionName });
  },

  // AI A/B Test Studio: recommend a test + generate/refine an HTML/CSS/JS variant.
  // payload: { request } for first generation, or { instruction, currentHtml } to refine.
  abtestStudio: (collectionName, payload = {}) => {
    return axios.post(`${BASE_URL}/abtest-studio`, { collectionName, ...payload });
  },

  // AI Landing Page Studio: redesign/build a full landing page from analytics.
  // payload: { request, pageUrl } for first generation, or { instruction, currentHtml } to refine.
  landingBuilder: (collectionName, payload = {}) => {
    return axios.post(`${BASE_URL}/landing-builder`, { collectionName, ...payload });
  },

  // Pages (incl. home) that would benefit from a redesign — for the pick-a-page step.
  landingCandidates: (collectionName) => {
    return axios.post(`${BASE_URL}/landing-builder/candidates`, { collectionName });
  },

  // Three data-backed ways to improve conversion (nudge / A/B test / landing).
  improveConversion: (collectionName) => {
    return axios.post(`${BASE_URL}/improve-conversion`, { collectionName });
  },

  sendFeedback: (collectionName, recommendationId, action) => {
    return axios.post(`${BASE_URL}/feedback`, { collectionName, recommendationId, action });
  },

  getTips: (collectionName, moduleName) => {
    return axios.get(`${BASE_URL}/tips/${collectionName}/${moduleName}`);
  },

  clearConversation: (collectionName) => {
    return axios.post(`${BASE_URL}/clear-conversation`, { collectionName });
  },

  getQueueStatus: () => {
    return axios.get(`${BASE_URL}/queue-status`);
  },

  checkEnabled: (collectionName) => {
    return axios.get(`${BASE_URL}/enabled/${collectionName}`);
  },

  toggleAgent: (collectionName, enabled, adminEmail) => {
    return axios.post(`${BASE_URL}/toggle`, { collectionName, enabled, adminEmail });
  },

  // Streaming helpers
  streamRecommendations: (collectionName) => {
    return `${BASE_URL}/recommendations/stream/${collectionName}`;
  },

  streamAsk: () => {
    return `${BASE_URL}/ask/stream`;
  },
};

// SSE stream consumer helper.
// A slow backend can close the connection (proxy/gateway timeout, wifi blip)
// before it ever emits a `result` event. When that happens the callers await
// this promise, so it MUST settle exactly once — otherwise the UI hangs on its
// loading state forever ("Still thinking…" / an endless spinner). We guard every
// path with `settle()` and arm a safety timeout as a last resort.
export function consumeSSEStream(url, options = {}) {
  const {
    method = "GET",
    body = null,
    onProgress,
    onResult,
    onError,
    // Generous ceiling: recommendations/ask are short, but never leave a hung
    // stream open indefinitely. Pass a larger value for long generations.
    timeoutMs = 120000,
  } = options;

  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;
    let cleanup = () => {};

    const finish = (fn, arg) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      cleanup();
      fn(arg);
    };

    const fail = (message) => {
      if (settled) return;
      if (onError) onError(message);
      finish(reject, message instanceof Error ? message : new Error(message));
    };

    const succeed = (data) => {
      if (settled) return;
      if (onResult) onResult(data);
      finish(resolve, data);
    };

    if (timeoutMs) {
      timeoutId = setTimeout(() => {
        fail("The request timed out. The server took too long to respond — please try again.");
      }, timeoutMs);
    }

    if (method === "GET") {
      const eventSource = new EventSource(url);
      cleanup = () => eventSource.close();

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "progress") {
            if (onProgress) onProgress(data.step, data.message);
          } else if (data.type === "result") {
            succeed(data.data);
          } else if (data.type === "error") {
            fail(data.error || "The server reported an error.");
          }
        } catch (e) {
          console.error("SSE parse error:", e);
        }
      };

      eventSource.onerror = () => {
        // EventSource fires this on a dropped/failed connection. If we already
        // got a result this is a no-op; otherwise surface it so the UI recovers.
        fail("Connection lost. Please try again.");
      };

      return eventSource;
    }

    // POST requests can't use EventSource, use fetch with a ReadableStream.
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (!response.ok || !response.body) {
          fail(`Request failed (${response.status || "no response"}).`);
          return;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        cleanup = () => reader.cancel().catch(() => {});

        const handleLine = (line) => {
          const dataLine = line.replace(/^data: /, "").trim();
          if (!dataLine) return;
          try {
            const data = JSON.parse(dataLine);
            if (data.type === "progress") {
              if (onProgress) onProgress(data.step, data.message);
            } else if (data.type === "result") {
              succeed(data.data);
            } else if (data.type === "error") {
              fail(data.error || "The server reported an error.");
            }
          } catch (e) {
            console.error("SSE parse error:", e);
          }
        };

        function read() {
          reader
            .read()
            .then(({ done, value }) => {
              if (settled) return;
              if (done) {
                // Stream closed. Flush anything buffered, then — if no result
                // ever arrived — treat the early close as a failure so callers
                // don't hang waiting on a promise that never settles.
                if (buffer.trim()) handleLine(buffer);
                fail("The connection closed before a full response was received. Please try again.");
                return;
              }
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split("\n\n");
              buffer = lines.pop() || ""; // Keep incomplete chunk
              for (const line of lines) handleLine(line);
              read();
            })
            .catch((err) => fail(err.message || "Stream read error."));
        }
        read();
      })
      .catch((err) => fail(err.message || "Network error."));
  });
}
